import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, ArrowLeft, MapPin, Mail, Phone, User } from "lucide-react";

const BookingStepContact = ({ formData, onUpdateField, onBack, onNext, isValid }) => {
  const { t } = useTranslation();

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="step-2-card">
      <CardHeader>
        <CardTitle className="text-2xl text-[#3D405B] font-['Playfair_Display']">
          {t("booking.step2.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-[#3D405B] font-medium mb-2 block">
              {t("booking.step2.fullName")}
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={formData.customer_name}
                onChange={(e) => onUpdateField("customer_name", e.target.value)}
                placeholder={t("booking.step2.fullNamePlaceholder")}
                data-testid="customer-name-input"
                className="pl-10"
              />
            </div>
          </div>
          <div>
            <Label className="text-[#3D405B] font-medium mb-2 block">
              {t("booking.step2.email")}
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => onUpdateField("customer_email", e.target.value)}
                placeholder={t("booking.step2.emailPlaceholder")}
                data-testid="customer-email-input"
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <div>
          <Label className="text-[#3D405B] font-medium mb-2 block">
            {t("booking.step2.phone")}
          </Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={formData.customer_phone}
              onChange={(e) => onUpdateField("customer_phone", e.target.value)}
              placeholder={t("booking.step2.phonePlaceholder")}
              data-testid="customer-phone-input"
              className="pl-10"
            />
          </div>
        </div>

        <div>
          <Label className="text-[#3D405B] font-medium mb-2 block">
            {t("booking.step2.venue")}
          </Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Textarea
              value={formData.venue_address}
              onChange={(e) => onUpdateField("venue_address", e.target.value)}
              placeholder={t("booking.step2.venuePlaceholder")}
              data-testid="venue-address-input"
              className="pl-10 min-h-[80px]"
            />
          </div>
        </div>

        <div>
          <Label className="text-[#3D405B] font-medium mb-2 block">
            {t("booking.step2.specialRequests")}
            {formData.event_type === "Other" && (
              <span className="text-[#E07A5F] ml-1">*</span>
            )}
          </Label>
          <Textarea
            value={formData.special_requests}
            onChange={(e) => onUpdateField("special_requests", e.target.value)}
            placeholder={
              formData.event_type === "Other"
                ? t("booking.step2.otherEventDescriptionPlaceholder")
                : t("booking.step2.specialRequestsPlaceholder")
            }
            data-testid="special-requests-input"
            className="min-h-[100px]"
          />
          {formData.event_type === "Other" && !formData.special_requests && (
            <p className="text-sm text-[#E07A5F] mt-1">{t("booking.step2.otherEventRequired")}</p>
          )}
        </div>

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={onBack}
            data-testid="back-btn"
            className="flex-1 py-6 rounded-full"
          >
            <ArrowLeft className="mr-2 w-5 h-5" />
            {t("booking.step2.backBtn")}
          </Button>
          <Button
            onClick={onNext}
            disabled={!isValid}
            data-testid="review-btn"
            className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white py-6 rounded-full"
          >
            {t("booking.step2.reviewBtn")}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingStepContact;
